import { API_ORIGIN, apiUrl } from "./apiOrigin";
// =============================================================================
// MhraAlerts.js  (v1.0 — live)
// v1.3 (v49 backend): the site drill-down's "Cascaded to" column now also
//   shows ROLES the alert was cascaded to (suffixed "(role)"), alongside
//   staff names. Two new columns, "Staff notified" and "% acknowledged",
//   count the handover_items raised by the cascade and how many have a
//   completedAt — click either to open a popup listing every notified staff
//   member with their task-created and task-completed dates, unacknowledged
//   rows highlighted red. "Alert date"/"Actioned" renamed to "Date created"/
//   "Date actioned" for clarity — same values as before.
// v1.2: search supports explicit AND / OR operators (AND binds tighter;
//   plain spaces mean AND). Tooltip + helper line explain the grammar, and a
//   readback under the search box shows exactly how the query was parsed,
//   e.g. (recall AND chloraprep) OR (valproate). Highlighting covers every
//   term from the query, whichever group matched.
// v1.1: "MHRA alerts in full" search — multi-word queries match only alerts
//   containing ALL the words (server-side, across title + summary + indexed
//   article text), and each result now shows the surrounding two-three
//   sentences per matched word with the search terms HIGHLIGHTED.
// v1.0: full implementation against headoffice.js v43.
//   - Branch list, one pharmacy per row: alerts actioned, outstanding and the
//     percentage actioned. Rows with ANY outstanding alert sit on a RED
//     background; fully-actioned rows on green.
//   - Clicking a SITE opens a popup listing every MHRA alert with the alert
//     date, title, the site's action date, what was done (affected / stock
//     details / action taken / not-affected reason) and the staff the alert
//     was cascaded to. A search box at the top filters by words in the title
//     or in the recorded response.
//   - Clicking an alert TITLE (anywhere) opens a scrollable popup with the
//     alert's full contents — summary plus the indexed gov.uk article text —
//     and a link to the source page.
//   - "MHRA alerts in full" lists every alert in the common db with a free-
//     text search across title + summary + indexed article text. The backend
//     indexes each article page ONCE (high-water mark), topping up with any
//     new alerts each time the screen is opened; an indexing progress line
//     shows while that runs.
// =============================================================================
import React, { useCallback, useEffect, useMemo, useState } from "react";
const API = apiUrl("");
const token = () => localStorage.getItem("token") || localStorage.getItem("authToken") || "";
async function post(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`
    },
    body: JSON.stringify(body || {})
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
  return d;
}
const S = {
  btn: (c, solid) => ({
    background: solid ? c : "#fff",
    color: solid ? "#fff" : c,
    border: `1px solid ${c}`,
    borderRadius: 6,
    padding: "6px 12px",
    fontSize: 12.5,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit"
  }),
  inp: {
    border: "1px solid #D9D9D9",
    borderRadius: 6,
    padding: "6px 9px",
    fontSize: 13,
    fontFamily: "inherit"
  },
  th: {
    textAlign: "left",
    padding: "8px 12px",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: "#5D5D5D",
    borderBottom: "2px solid #F0F0F0",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    background: "#FAFAFA",
    zIndex: 2
  },
  td: {
    padding: "8px 12px",
    fontSize: 13,
    borderBottom: "1px solid #f0f1f3",
    verticalAlign: "top"
  }
};
const dmy = iso => iso ? new Date(iso).toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric"
}) : "—";
const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9500
};
const classChip = lvl => lvl == null ? null : <span style={{
  fontSize: 10.5,
  fontWeight: 800,
  padding: "1px 7px",
  borderRadius: 4,
  marginRight: 6,
  background: lvl <= 2 ? "#FFF1F0" : "#FFF7E6",
  color: lvl <= 2 ? "#A8071A" : "#AD4E00",
  whiteSpace: "nowrap"
}}>
    Class {lvl}
  </span>;
const catLabel = c => c ? String(c).replace(/_/g, " ").toLowerCase() : "";

// Highlight every occurrence of the search terms inside a snippet block.
const escRe = t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
function Highlighted({
  text,
  terms
}) {
  if (!terms?.length) return <>{text}</>;
  const re = new RegExp(`(${terms.map(escRe).join("|")})`, "ig");
  const parts = String(text).split(re);
  return <>
      {parts.map((p, i) => terms.some(t => t.toLowerCase() === p.toLowerCase()) ? <mark key={i} style={{
      background: "#FFE58F",
      fontWeight: 700,
      padding: "0 1px",
      borderRadius: 2
    }}>{p}</mark> : <span key={i}>{p}</span>)}
    </>;
}

/* ---------------------------------------------------------------------------
   Alert content popup — scrollable full contents of one alert. Fetches the
   indexed article text on open.
   --------------------------------------------------------------------------- */
function AlertContentDialog({
  alertId,
  onClose
}) {
  const [alert, setAlert] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let dead = false;
    post("/api/headoffice/mhra/alert", {
      alertId
    }).then(d => {
      if (!dead) setAlert(d.alert);
    }).catch(e => {
      if (!dead) setErr(e.message);
    });
    return () => {
      dead = true;
    };
  }, [alertId]);
  return <div style={{
    ...overlay,
    zIndex: 9560
  }} onClick={onClose}>
      <div style={{
      background: "#fff",
      borderRadius: 8,
      width: "min(860px, 94vw)",
      maxHeight: "88vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    }} onClick={e => e.stopPropagation()}>
        <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 10,
        padding: "14px 18px 10px",
        borderBottom: "1px solid #F0F0F0"
      }}>
          <div style={{
          fontWeight: 800,
          fontSize: 15,
          lineHeight: 1.35
        }}>
            {alert ? <>{classChip(alert.classLevel)}{alert.title}</> : "MHRA alert"}
          </div>
          <button style={S.btn("#5D5D5D")} onClick={onClose}>Close</button>
        </div>
        <div style={{
        overflowY: "auto",
        padding: "12px 18px 18px"
      }}>
          {err && <div style={{
          color: "#CF1322",
          fontSize: 13
        }}>{err}</div>}
          {!alert && !err && <div style={{
          color: "#5D5D5D",
          fontSize: 13
        }}>Loading alert…</div>}
          {alert && <>
              <div style={{
            fontSize: 12,
            color: "#5D5D5D",
            marginBottom: 8
          }}>
                {dmy(alert.alertDate)}{alert.category ? ` · ${catLabel(alert.category)}` : ""}
                {alert.articleUrl && <> · <a href={alert.articleUrl} target="_blank" rel="noreferrer" style={{
                color: "#0E8CFF"
              }}>
                    open on gov.uk ↗</a></>}
              </div>
              {alert.summary && <div style={{
            fontSize: 13.5,
            background: "#FAFAFA",
            border: "1px solid #F0F0F0",
            borderRadius: 6,
            padding: "10px 12px",
            marginBottom: 12,
            lineHeight: 1.5
          }}>
                  {alert.summary}
                </div>}
              {alert.content ? <div style={{
            fontSize: 13,
            lineHeight: 1.65,
            whiteSpace: "pre-wrap",
            color: "#262626"
          }}>{alert.content}</div> : <div style={{
            fontSize: 12.5,
            color: "#8C8C8C"
          }}>
                    Full article text not indexed yet — it will be picked up on the next visit to this screen.
                    Use the gov.uk link above in the meantime.
                  </div>}
            </>}
        </div>
      </div>
    </div>;
}

/* ---------------------------------------------------------------------------
   Cascade drill-down (v49) — every staff member a given alert's handover
   items were raised for at this site, with their acknowledgement status.
   Opened by clicking either the "Staff notified" or "% acknowledged" cell.
   Rows with no completed date (not yet acknowledged) sit on a red background,
   same convention as the outstanding-alert rows in SiteDialog below.
   --------------------------------------------------------------------------- */
function CascadeDetailDialog({
  dbName,
  mrhaAlertId,
  title,
  onClose
}) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let dead = false;
    post("/api/headoffice/mhra/cascade-detail", {
      dbName,
      mrhaAlertId
    }).then(d => {
      if (!dead) setItems(d.items || []);
    }).catch(e => {
      if (!dead) setErr(e.message);
    });
    return () => {
      dead = true;
    };
  }, [dbName, mrhaAlertId]);
  return <div style={{
    ...overlay,
    zIndex: 9580
  }} onClick={onClose}>
      <div style={{
      background: "#fff",
      borderRadius: 8,
      width: "min(620px, 94vw)",
      maxHeight: "80vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    }} onClick={e => e.stopPropagation()}>
        <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 10,
        padding: "14px 18px 10px",
        borderBottom: "1px solid #F0F0F0"
      }}>
          <div>
            <div style={{
            fontWeight: 800,
            fontSize: 15
          }}>Cascade notification</div>
            <div style={{
            fontSize: 12,
            color: "#5D5D5D",
            marginTop: 2
          }}>{title}</div>
          </div>
          <button style={S.btn("#5D5D5D")} onClick={onClose}>Close</button>
        </div>
        <div style={{
        overflowY: "auto",
        flex: 1
      }}>
          {err && <div style={{
          color: "#CF1322",
          fontSize: 13,
          padding: 16
        }}>{err}</div>}
          {!items && !err && <div style={{
          color: "#0E8CFF",
          fontWeight: 600,
          fontSize: 13,
          padding: 16
        }}>Loading…</div>}
          {items && <table style={{
          width: "100%",
          borderCollapse: "collapse"
        }}>
              <thead>
                <tr>
                  <th style={S.th}>Staff member</th>
                  <th style={{
                ...S.th,
                width: 130
              }}>Task created</th>
                  <th style={{
                ...S.th,
                width: 130
              }}>Task completed</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => <tr key={i} style={{
              background: it.acknowledged ? undefined : "#FFF1F0"
            }}>
                    <td style={{
                ...S.td,
                fontWeight: 700
              }}>{it.staffName}</td>
                    <td style={{
                ...S.td,
                whiteSpace: "nowrap"
              }}>{dmy(it.createdAt)}</td>
                    <td style={{
                ...S.td,
                whiteSpace: "nowrap",
                fontWeight: it.acknowledged ? 400 : 700,
                color: it.acknowledged ? undefined : "#CF1322"
              }}>
                      {it.acknowledged ? dmy(it.completedAt) : "Not yet acknowledged"}
                    </td>
                  </tr>)}
                {items.length === 0 && <tr><td colSpan={3} style={{
                ...S.td,
                color: "#5D5D5D"
              }}>No staff have been notified for this alert.</td></tr>}
              </tbody>
            </table>}
        </div>
      </div>
    </div>;
}

/* ---------------------------------------------------------------------------
   Site drill-down — every alert with this site's action detail. Search box
   filters on words in the title OR the recorded response.
   --------------------------------------------------------------------------- */
function SiteDialog({
  dbName,
  pharmacyName,
  onClose,
  onOpenAlert,
  inspectorExplanation = false
}) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [detailDlg, setDetailDlg] = useState(null); // { mrhaAlertId, title }
  useEffect(() => {
    let dead = false;
    post("/api/headoffice/mhra/site", {
      dbName
    }).then(d => {
      if (!dead) setRows(d.rows || []);
    }).catch(e => {
      if (!dead) setErr(e.message);
    });
    return () => {
      dead = true;
    };
  }, [dbName]);
  const shown = useMemo(() => {
    if (!rows) return null;
    const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return rows;
    return rows.filter(r => {
      const act = r.action || {};
      const hay = [r.title, act.affectedStockDetails, act.actionTakenByStaff, act.notAffectedReason, ...(act.staffNames || []), ...(act.roleNames || [])].filter(Boolean).join(" ").toLowerCase();
      return terms.every(t => hay.includes(t));
    });
  }, [rows, q]);
  const actionText = a => {
    if (!a) return null;
    const bits = [];
    if (a.isAffected) {
      bits.push(<div key="af" style={{
        color: "#CF1322",
        fontWeight: 700
      }}>Stock affected</div>);
      if (a.affectedStockDetails) bits.push(<div key="sd">{a.affectedStockDetails}</div>);
      if (a.actionTakenByStaff) bits.push(<div key="at"><b>Action taken:</b> {a.actionTakenByStaff}</div>);
    } else {
      bits.push(<div key="na" style={{
        color: "#389E0D",
        fontWeight: 700
      }}>Not affected</div>);
      if (a.notAffectedReason) bits.push(<div key="nr">{a.notAffectedReason}</div>);
      if (a.actionTakenByStaff) bits.push(<div key="at2"><b>Action taken:</b> {a.actionTakenByStaff}</div>);
    }
    return bits;
  };
  return <div style={overlay} onClick={onClose}>
      <div style={{
      background: "#fff",
      borderRadius: 8,
      width: "min(1240px, 96vw)",
      height: "88vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    }} onClick={e => e.stopPropagation()}>
        <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
        padding: "12px 18px",
        borderBottom: "1px solid #F0F0F0",
        flexWrap: "wrap"
      }}>
          <div style={{
          fontWeight: 800,
          fontSize: 15
        }}>MHRA alerts — {pharmacyName}</div>
          <div style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flex: "1 1 300px",
          maxWidth: 520
        }}>
            <input style={{
            ...S.inp,
            flex: 1
          }} value={q} onChange={e => setQ(e.target.value)} placeholder="Search alert titles or recorded responses…" />
            {q && <button style={S.btn("#5D5D5D")} onClick={() => setQ("")}>Clear</button>}
          </div>
          <button style={S.btn("#5D5D5D")} onClick={onClose}>Close</button>
        </div>
        <div style={{
        overflowY: "auto",
        flex: 1
      }}>
          {inspectorExplanation && <section aria-label="How MHRA alerts are managed" style={{
          background: "#F0F5FF",
          border: "1px solid #ADC6FF",
          borderRadius: 8,
          padding: "12px 16px",
          margin: "12px 18px",
          color: "#262626",
          fontSize: 14,
          lineHeight: 1.6
        }}>
            <strong>How MHRA alerts are managed</strong>
            <p style={{
            margin: "6px 0 0"
          }}>Incoming MHRA alerts are sent automatically to the pharmacy for the pharmacist to review and process. The pharmacist reviews each alert and cascades it to relevant staff members, selected by name or role. Staff receive these as tasks in their staff app, where they review the alert and acknowledge receipt. The grid below shows the recorded pharmacy response, who was notified and their acknowledgements.</p>
          </section>}
          {err && <div style={{
          color: "#CF1322",
          fontSize: 13,
          padding: 16
        }}>{err}</div>}
          {!rows && !err && <div style={{
          color: "#0E8CFF",
          fontWeight: 600,
          fontSize: 13,
          padding: 16
        }}>Loading alerts for {pharmacyName}…</div>}
          {shown && <table style={{
          width: "100%",
          borderCollapse: "collapse"
        }}>
              <thead>
                <tr>
                  <th style={{
                ...S.th,
                width: 96
              }}>Date created</th>
                  <th style={S.th}>Title</th>
                  <th style={{
                ...S.th,
                width: 96
              }}>Date actioned</th>
                  <th style={{
                ...S.th,
                width: 320
              }}>Action recorded</th>
                  <th style={{
                ...S.th,
                width: 190
              }}>Cascaded to</th>
                  <th style={{
                ...S.th,
                width: 100,
                textAlign: "right"
              }}>Staff notified</th>
                  <th style={{
                ...S.th,
                width: 110,
                textAlign: "right"
              }}>% acknowledged</th>
                </tr>
              </thead>
              <tbody>
                {shown.map(r => {
              const cascadeNames = [...(r.action?.staffNames || []), ...(r.action?.roleNames || []).map(rn => `${rn} (role)`)];
              const clickable = r.staffNotifiedCount > 0;
              const openDetail = () => clickable && setDetailDlg({
                mrhaAlertId: r.mrhaAlertId,
                title: r.title
              });
              return <tr key={r.alertId} style={{
                background: r.actioned ? undefined : "#FFF1F0"
              }}>
                      <td style={{
                  ...S.td,
                  whiteSpace: "nowrap"
                }}>{dmy(r.alertDate)}</td>
                      <td style={S.td}>
                        {classChip(r.classLevel)}
                        <span onClick={() => onOpenAlert(r.alertId)} title="Click to read the full alert" style={{
                    color: "#0E8CFF",
                    cursor: "pointer",
                    textDecoration: "underline dotted",
                    textUnderlineOffset: 3
                  }}>
                          {r.title}
                        </span>
                      </td>
                      <td style={{
                  ...S.td,
                  whiteSpace: "nowrap",
                  fontWeight: 700,
                  color: r.actioned ? "#389E0D" : "#CF1322"
                }}>
                        {r.actioned ? dmy(r.action.date) : "Outstanding"}
                      </td>
                      <td style={{
                  ...S.td,
                  fontSize: 12.5,
                  lineHeight: 1.45
                }}>{actionText(r.action) || <span style={{
                    color: "#8C8C8C"
                  }}>—</span>}</td>
                      <td style={{
                  ...S.td,
                  fontSize: 12.5
                }}>
                        {cascadeNames.length ? cascadeNames.join(", ") : <span style={{
                    color: "#8C8C8C"
                  }}>—</span>}
                      </td>
                      <td style={{
                  ...S.td,
                  textAlign: "right",
                  fontWeight: 700,
                  cursor: clickable ? "pointer" : "default",
                  color: clickable ? "#0E8CFF" : "#8C8C8C",
                  textDecoration: clickable ? "underline dotted" : "none",
                  textUnderlineOffset: 3
                }} onClick={openDetail} title={clickable ? "Click to see who was notified and whether they've acknowledged" : "No staff notified yet"}>
                        {r.staffNotifiedCount || 0}
                      </td>
                      <td style={{
                  ...S.td,
                  textAlign: "right",
                  fontWeight: 700,
                  cursor: clickable ? "pointer" : "default",
                  color: !clickable ? "#8C8C8C" : r.notificationAckPct === 100 ? "#389E0D" : "#CF1322",
                  textDecoration: clickable ? "underline dotted" : "none",
                  textUnderlineOffset: 3
                }} onClick={openDetail} title={clickable ? "Click to see who was notified and whether they've acknowledged" : "No staff notified yet"}>
                        {r.notificationAckPct == null ? "—" : `${r.notificationAckPct}%`}
                      </td>
                    </tr>;
            })}
                {shown.length === 0 && <tr><td colSpan={7} style={{
                ...S.td,
                color: "#5D5D5D"
              }}>No alerts match that search.</td></tr>}
              </tbody>
            </table>}
        </div>
      </div>
      {detailDlg && <CascadeDetailDialog dbName={dbName} mrhaAlertId={detailDlg.mrhaAlertId} title={detailDlg.title} onClose={() => setDetailDlg(null)} />}
    </div>;
}

/* ---------------------------------------------------------------------------
   "MHRA alerts in full" — every alert in the common db, free-text search
   across title + summary + indexed article text (search runs server-side so
   it covers the indexed contents without shipping them all to the browser).
   --------------------------------------------------------------------------- */
function AllAlertsDialog({
  onClose,
  onOpenAlert
}) {
  const [data, setData] = useState(null); // { rows, total, indexing }
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const run = useCallback(async query => {
    setBusy(true);
    setErr(null);
    try {
      const d = await post("/api/headoffice/mhra/all", {
        q: query || ""
      });
      d.terms = [...new Set((d.groups || []).flat())];
      setData(d);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => {
    run("");
  }, [run]);
  const idx = data?.indexing;
  return <div style={overlay} onClick={onClose}>
      <div style={{
      background: "#fff",
      borderRadius: 8,
      width: "min(1240px, 96vw)",
      height: "88vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    }} onClick={e => e.stopPropagation()}>
        <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
        padding: "12px 18px",
        borderBottom: "1px solid #F0F0F0",
        flexWrap: "wrap"
      }}>
          <div style={{
          fontWeight: 800,
          fontSize: 15
        }}>MHRA alerts in full{data ? ` — ${data.total} alerts` : ""}</div>
          <form style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flex: "1 1 320px",
          maxWidth: 560
        }} onSubmit={e => {
          e.preventDefault();
          run(q);
        }}>
            <input style={{
            ...S.inp,
            flex: 1
          }} value={q} onChange={e => setQ(e.target.value)} placeholder="e.g.  chloraprep AND sterility   \u00b7   apixaban OR rivaroxaban" title={"Plain words (or AND between them) must ALL appear in the alert \u2014 title, summary or full article text.\nOR gives alternatives: apixaban OR rivaroxaban matches either.\nAND binds tighter than OR: recall AND chloraprep OR valproate = (recall AND chloraprep) OR valproate."} />
            <button type="submit" style={S.btn("#0E8CFF", true)} disabled={busy}>{busy ? "Searching…" : "Search"}</button>
            {q && <button type="button" style={S.btn("#5D5D5D")} onClick={() => {
            setQ("");
            run("");
          }}>Clear</button>}
          </form>
          <button style={S.btn("#5D5D5D")} onClick={onClose}>Close</button>
        </div>
        <div style={{
        padding: "5px 18px",
        fontSize: 11.5,
        color: "#5D5D5D",
        borderBottom: "1px solid #f0f1f3"
      }}>
          Plain words or <b>AND</b> = every word must appear somewhere in the alert; <b>OR</b> = either side matches; AND binds tighter than OR.
          {data?.groups?.length > 0 && <span style={{
          marginLeft: 8,
          color: "#0E8CFF",
          fontWeight: 700
        }} title="How your query was interpreted">
              Reading your search as: {data.groups.map(g => `(${g.join(" AND ")})`).join(" OR ")}
            </span>}
        </div>
        {idx && idx.indexed < idx.total && <div style={{
        padding: "6px 18px",
        fontSize: 12,
        color: "#AD4E00",
        background: "#FFF7E6",
        borderBottom: "1px solid #FFE58F"
      }}>
            Indexing article text: {idx.indexed} of {idx.total} alerts indexed so far
            {idx.running ? " — indexing is running in the background; searches already cover every title and summary, and the indexed articles." : "."}
            {" "}Re-run the search in a minute to cover more.
          </div>}
        <div style={{
        overflowY: "auto",
        flex: 1
      }}>
          {err && <div style={{
          color: "#CF1322",
          fontSize: 13,
          padding: 16
        }}>{err}</div>}
          {!data && !err && <div style={{
          color: "#0E8CFF",
          fontWeight: 600,
          fontSize: 13,
          padding: 16
        }}>Loading alerts…</div>}
          {data && <table style={{
          width: "100%",
          borderCollapse: "collapse"
        }}>
              <thead>
                <tr>
                  <th style={{
                ...S.th,
                width: 96
              }}>Date</th>
                  <th style={S.th}>Title</th>
                  <th style={{
                ...S.th,
                width: 230
              }}>Category</th>
                  <th style={{
                ...S.th,
                width: 70
              }}>Indexed</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(r => <tr key={r.alertId}>
                    <td style={{
                ...S.td,
                whiteSpace: "nowrap"
              }}>{dmy(r.alertDate)}</td>
                    <td style={S.td}>
                      {classChip(r.classLevel)}
                      <span onClick={() => onOpenAlert(r.alertId)} title="Click to read the full alert" style={{
                  color: "#0E8CFF",
                  cursor: "pointer",
                  textDecoration: "underline dotted",
                  textUnderlineOffset: 3
                }}>
                        {r.title}
                      </span>
                      {(!r.snippets || r.snippets.length === 0) && r.summary && <div style={{
                  fontSize: 12,
                  color: "#5D5D5D",
                  marginTop: 2
                }}>{r.summary.slice(0, 220)}{r.summary.length > 220 ? "\u2026" : ""}</div>}
                      {r.snippets && r.snippets.map((sn, i) => <div key={i} style={{
                  fontSize: 12,
                  color: "#434343",
                  marginTop: 4,
                  lineHeight: 1.5,
                  background: "#FAFAFA",
                  border: "1px solid #F0F0F0",
                  borderRadius: 5,
                  padding: "5px 8px"
                }}>
                          {"\u2026"}<Highlighted text={sn} terms={data.terms} />{"\u2026"}
                        </div>)}
                    </td>
                    <td style={{
                ...S.td,
                fontSize: 12,
                textTransform: "capitalize"
              }}>{catLabel(r.category)}</td>
                    <td style={{
                ...S.td,
                fontSize: 12,
                color: r.indexed ? "#389E0D" : "#8C8C8C"
              }}>{r.indexed ? "✓" : "—"}</td>
                  </tr>)}
                {data.rows.length === 0 && <tr><td colSpan={4} style={{
                ...S.td,
                color: "#5D5D5D"
              }}>No alerts match that search.</td></tr>}
              </tbody>
            </table>}
        </div>
      </div>
    </div>;
}

/* ---------------------------------------------------------------------------
   Main view — branch list, one per row.
   --------------------------------------------------------------------------- */
export default function MhraAlerts() {
  const [data, setData] = useState(null); // { totalAlerts, rows, rollup }
  const [err, setErr] = useState(null);
  const [siteDlg, setSiteDlg] = useState(null); // { dbName, pharmacyName }
  const [alertDlg, setAlertDlg] = useState(null); // alertId
  const [showAll, setShowAll] = useState(false);
  const load = useCallback(() => {
    setErr(null);
    post("/api/headoffice/mhra/summary", {}).then(setData).catch(e => setErr(e.message));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  return <div style={{
    padding: "16px 20px"
  }}>
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap"
    }}>
        <div>
          <h2 style={{
          margin: "0 0 4px",
          fontSize: 20
        }}>MHRA alerts</h2>
          <div style={{
          fontSize: 13,
          color: "#5D5D5D"
        }}>
            {data ? <>
              {data.totalAlerts} live alerts ·{" "}
              <span style={{
              color: "#389E0D",
              fontWeight: 700
            }}>{data.rollup.sitesUpToDate} site{data.rollup.sitesUpToDate === 1 ? "" : "s"} fully actioned</span>
              {" · "}
              <span style={{
              color: data.rollup.sitesOutstanding ? "#CF1322" : "#5D5D5D",
              fontWeight: 700
            }}>
                {data.rollup.sitesOutstanding} with outstanding actions
              </span>
            </> : "Each site acknowledges every alert and records what was done. Click a site for its full alert-by-alert record."}
          </div>
        </div>
        <div style={{
        display: "flex",
        gap: 8
      }}>
          <button style={S.btn("#0E8CFF", true)} onClick={() => setShowAll(true)} title="Every MHRA alert in the shared database, with free-text search across titles, summaries and the full article text.">
            MHRA alerts in full
          </button>
          <button style={S.btn("#5D5D5D")} onClick={load}>↻ Refresh</button>
        </div>
      </div>

      <div style={{
      marginTop: 14,
      overflowX: "auto",
      border: "1px solid #F0F0F0",
      borderRadius: 8,
      background: "#fff",
      maxHeight: "72vh",
      overflowY: "auto"
    }}>
        <table style={{
        width: "100%",
        borderCollapse: "collapse"
      }}>
          <thead>
            <tr>
              <th style={S.th}>Pharmacy</th>
              <th style={{
              ...S.th,
              textAlign: "right"
            }}>Alerts actioned</th>
              <th style={{
              ...S.th,
              textAlign: "right"
            }}>Outstanding</th>
              <th style={{
              ...S.th,
              textAlign: "right"
            }}>% actioned</th>
              <th style={{
              ...S.th,
              textAlign: "right"
            }} title="This is the total number of alerts sent to members of staff to read a specific MHRA alert">
                Total staff cascaded to
              </th>
              <th style={{
              ...S.th,
              textAlign: "right"
            }} title="This is the percentage of staff alerts which have been acknowledged">
                % acknowledged
              </th>
            </tr>
          </thead>
          <tbody>
            {!data && !err && <tr><td colSpan={6} style={{
              ...S.td,
              color: "#0E8CFF",
              fontWeight: 600
            }}>
                Loading… checking every site's alert logs — this can take a few seconds.
              </td></tr>}
            {err && <tr><td colSpan={6} style={{
              ...S.td,
              color: "#CF1322"
            }}>{err}</td></tr>}
            {data && data.rows.map(r => {
            const clean = r.outstanding === 0 && r.totalAlerts > 0;
            return <tr key={r.dbName} onClick={() => setSiteDlg({
              dbName: r.dbName,
              pharmacyName: r.pharmacyName
            })} title={`Click for ${r.pharmacyName}'s alert-by-alert record`} style={{
              cursor: "pointer",
              background: r.outstanding > 0 ? "#FFF1F0" : clean ? "#F6FFED" : undefined
            }}>
                  <td style={{
                ...S.td,
                fontWeight: 700
              }}>{r.pharmacyName}</td>
                  <td style={{
                ...S.td,
                textAlign: "right",
                color: "#389E0D",
                fontWeight: 700
              }}>
                    {r.actioned} <span style={{
                  color: "#8C8C8C",
                  fontWeight: 400
                }}>/ {r.totalAlerts}</span>
                  </td>
                  <td style={{
                ...S.td,
                textAlign: "right",
                fontWeight: 700,
                color: r.outstanding > 0 ? "#CF1322" : "#262626"
              }}>
                    {r.outstanding}
                  </td>
                  <td style={{
                ...S.td,
                textAlign: "right",
                fontWeight: 700
              }}>{r.pct}%</td>
                  <td style={{
                ...S.td,
                textAlign: "right",
                fontWeight: 700
              }}>{r.staffCascadedCount || 0}</td>
                  <td style={{
                ...S.td,
                textAlign: "right",
                fontWeight: 700,
                color: r.staffAckPct == null ? "#8C8C8C" : r.staffAckPct === 100 ? "#389E0D" : "#CF1322"
              }}>
                    {r.staffAckPct == null ? "—" : `${r.staffAckPct}%`}
                  </td>
                </tr>;
          })}
            {data && data.rows.length === 0 && <tr><td colSpan={6} style={{
              ...S.td,
              color: "#5D5D5D"
            }}>No pharmacies in scope.</td></tr>}
          </tbody>
        </table>
      </div>

      {siteDlg && <SiteDialog {...siteDlg} onClose={() => {
      setSiteDlg(null);
      load();
    }} onOpenAlert={id => setAlertDlg(id)} />}
      {showAll && <AllAlertsDialog onClose={() => setShowAll(false)} onOpenAlert={id => setAlertDlg(id)} />}
      {alertDlg && <AlertContentDialog alertId={alertDlg} onClose={() => setAlertDlg(null)} />}
    </div>;
}
// Branch-scoped inspector entry point; shares the existing MHRA detail UI.
export function InspectorMhraSite(props) {
  const [alert, setAlert] = useState(null);
  return <><SiteDialog {...props} inspectorExplanation onOpenAlert={setAlert} />{alert && <AlertContentDialog alertId={alert} onClose={() => setAlert(null)} />}</>;
}
